'use client';

import React, { useRef, useState } from 'react';

// The number box between a card's − and + buttons. The shopper can also TYPE
// a quantity (founder, 2026-10-02); − and + still work exactly as before.
//
// The typed text is a local draft and only reaches the card's quantity on
// commit (blur or Enter), so a half-typed «» or «0» never becomes the quantity.
// Anything that isn't a whole number ≥ 1 reverts to the previous value.
// Clicking «Add to cart» while typing still uses the typed number: the button's
// mousedown blurs the box first, which commits.
//
// Font: ≥16px on touch screens, because iOS Safari zooms the whole page into
// any focused input smaller than that. Desktop keeps the stepper's own size.

interface QuantityInputProps {
  value: number;
  onCommit: (quantity: number) => void;
  /** Visual classes of the box it replaces (size, border, font). */
  className: string;
  /** Digits allowed (3 → up to 999). */
  maxDigits?: number;
  /**
   * Weighed products (2026-10-09): accept a decimal («0,4» or «0.4») and show
   * the value through `format`. Omitted → whole numbers only, as before.
   */
  decimals?: number;
  format?: (quantity: number) => string;
}

export function QuantityInput({ value, onCommit, className, maxDigits = 3, decimals = 0, format }: QuantityInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);

  const update = (next: string | null) => {
    draftRef.current = next;
    setDraft(next);
  };

  const commit = () => {
    const typed = draftRef.current;
    update(null);
    if (typed === null) return;
    if (decimals > 0) {
      // The card snaps it to a valid weight (quantityRules.snapQuantity).
      const n = parseFloat(typed.replace(',', '.'));
      if (Number.isFinite(n) && n > 0 && n !== value) onCommit(n);
      return;
    }
    const n = parseInt(typed, 10);
    if (Number.isFinite(n) && n >= 1 && n !== value) onCommit(n);
  };

  return (
    <input
      type="text"
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      pattern={decimals > 0 ? undefined : '[0-9]*'}
      autoComplete="off"
      aria-label="Quantity"
      value={draft ?? (format ? format(value) : String(value))}
      onChange={(e) =>
        update(
          decimals > 0
            ? // digits plus ONE decimal separator, at most `decimals` places after it
              (e.target.value.replace(/[^\d.,]/g, '').match(new RegExp(`^\\d{0,${maxDigits}}(?:[.,]\\d{0,${decimals}})?`))?.[0] ?? '')
            : e.target.value.replace(/\D/g, '').slice(0, maxDigits),
        )
      }
      onFocus={(e) => e.currentTarget.select()}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          // Undo the typing; don't let Escape also close the whole modal.
          e.preventDefault();
          update(null);
          e.currentTarget.blur();
        }
      }}
      className={`${className} p-0 text-center bg-white outline-none focus:border-black tabular-nums [@media(pointer:coarse)]:text-[16px]`}
    />
  );
}
